import { ToastService } from "@shared/lib/services";
import type { EditorCommentsConfig } from "@shared/ui/Editor/lib/editorComments";
import { useEditorDocumentImport } from "@shared/ui/Editor/lib/useEditorDocumentImport";
import { DocumentImportOverlay } from "@shared/ui/Editor/ui/DocumentImportOverlay";
import { DocumentUploadButton } from "@shared/ui/Editor/ui/DocumentUploadButton";
import type { EditorHandle } from "@shared/ui/Editor/ui";
import Editor from "@shared/ui/Editor/ui";
import { IScripting } from "@shared/lib/types/briefCampains";
import classNames from "classnames";
import type { RefObject } from "react";
import styles from "./styles.module.scss";

interface ScriptHeaderProps {
  script: IScripting;
  isDownloading: boolean;
  isSaving?: boolean;
  savedFlash?: boolean;
  editorRef: RefObject<EditorHandle | null>;
  commentsConfig: EditorCommentsConfig;
  onEditorChange?: () => void;
  onEditorReady?: () => void;
  resetSnapshot?: (html: string) => void;
  onMediaUploadStart?: () => void;
  onMediaUploadComplete?: () => void;
}

export function ScriptHeader(props: ScriptHeaderProps) {
  const {
    script,
    isDownloading,
    isSaving = false,
    savedFlash = false,
    editorRef,
    commentsConfig,
    onEditorChange,
    onEditorReady,
    resetSnapshot,
    onMediaUploadStart,
    onMediaUploadComplete,
  } = props;

  const documentImport = useEditorDocumentImport({
    editorRef,
    disabled: isDownloading,
    showStatus: (message, tone) => {
      if (tone === "err") ToastService.error(message);
      else ToastService.success(message);
    },
    onContentChange: onEditorChange,
    onImportComplete: onMediaUploadComplete,
  });

  const handleEditorReady = () => {
    const html =
      editorRef.current?.getHTML({ syncTokens: false }) ??
      script.concept ??
      "";
    resetSnapshot?.(html);
    onEditorReady?.();
  };

  return (
    <div id="concept-card" className={styles.conceptCard}>
      {(savedFlash || isSaving) && (
        <span
          className={classNames(styles.editorStatusBadge, {
            [styles.savedBadge]: savedFlash,
            [styles.savingBadge]: isSaving && !savedFlash,
          })}
        >
          {savedFlash ? "Saved" : "Saving…"}
        </span>
      )}
      <div className={styles.conceptToolbar}>
        <DocumentUploadButton
          fileInputRef={documentImport.fileInputRef}
          accept={documentImport.accept}
          disabled={isDownloading || documentImport.isImporting}
          isImporting={documentImport.isImporting}
          onFileInputChange={documentImport.onFileInputChange}
          onClick={documentImport.openFilePicker}
        />
      </div>
      <div
        className={`${styles.conceptEditorWrap} ${documentImport.isDragOver ? styles.conceptEditorWrapDrag : ""}`}
        onDragOverCapture={documentImport.onDragOver}
        onDragLeave={documentImport.onDragLeave}
        onDropCapture={documentImport.onDrop}
        onPasteCapture={documentImport.onPaste}
      >
        <DocumentImportOverlay
          isImporting={documentImport.isImporting}
          isDragOver={documentImport.isDragOver}
        />
        <Editor
          key={script._id}
          ref={editorRef}
          compact
          className={styles.conceptEditor}
          initialHtml={script.concept || ""}
          editable={!isDownloading}
          allowDocumentImport
          comments={commentsConfig}
          onChange={onEditorChange}
          onReady={handleEditorReady}
          onMediaUploadStart={onMediaUploadStart}
          onMediaUploadComplete={onMediaUploadComplete}
        />
      </div>
    </div>
  );
}

export default ScriptHeader;
